import { WILDCARD, type RoleGrantSummary } from '@/models/permission';

/**
 * URL-backed list filters, mirroring the endpoint's own query params. Applied by
 * the backend, never in the browser: the list is paginated, so filtering the page
 * in hand would search a slice and report the rest as absent.
 */
export interface MemberFilters {
  /**
   * Case-insensitive **prefix** match on display_name or email — not a substring
   * search, and it does not look at login. That is the SQL admin-api runs
   * (user_repository.go).
   */
  search?: string;
}

// Whether a Grant covers every Sandbox — what the Layers icon stands for, as
// opposed to the Box of a single Sandbox, and the only reason to highlight a scope
// as broader than the others.
//
// Deliberately blind to game_code: the caller has already established which Game is
// in view — a Game route, or the Game tab in "My account" — so a Nacon-wide
// ('*', 'SDB001') Grant confers exactly SDB001 there and must read like any other
// single-Sandbox Grant. A predicate that also fired on a wildcard game_code got the
// label, the icon and the highlight to disagree.
//
// Typed on the one field they read, so both the viewer's own Grants and another
// user's can use them.
export const isAllSandboxes = ({ sandbox_code }: { sandbox_code: string }) =>
  sandbox_code === WILDCARD;

// A ('*', '*') Grant means every Sandbox of the Game in view; naming the Game is the
// caller's job, not this label's.
export const scopeLabel = (grant: { sandbox_code: string }) =>
  isAllSandboxes(grant) ? 'All Sandboxes' : grant.sandbox_code;

/**
 * The natural key of a Grant. A Role can be granted once per scope, so this is
 * unique per user — the React key of every Grant row, and the id the table
 * selection tracks.
 */
export const grantKey = ({ id, game_code, sandbox_code }: RoleGrantSummary) =>
  `${id}-${game_code}-${sandbox_code}`;
